/**
 * Thin fetch wrapper for the JeanieIQ REST API.
 *
 * Responsibilities:
 *  - Prefix every relative URL with the configured API base (defaults to the
 *    same origin so dev/prod can both work without env wrangling).
 *  - Attach the JSON `Content-Type` and `Authorization` headers when an
 *    access token is present in localStorage.
 *  - Unwrap the standard `{ success, data | error }` envelope and throw an
 *    `ApiError` on failure so react-query treats it as an error and surfaces
 *    the typed code/message to consumers.
 *
 * The wrapper deliberately stays small. Anything beyond plain JSON ↔ JSON
 * (file upload, server-sent events, etc.) should grow its own helper next
 * to this file rather than overloading this function.
 */

import { clearAccessToken, clearSessionAfter401, getAccessToken, inEmployeeApp } from './auth';
import { describeValidationIssues } from './validationIssues';
import { currentScreen } from './clientScreen';
import { foreignReply, newRequestId, readReply, unreachableDetail } from './requestTrace';
import { showInPage } from './documentViewer';
// 2026-06-03 Driver Tablet T1.2 phase 2 — when no JWT is present
// (cold tablet, no signed-in user), fall back to the driver-tablet
// device token stored at provisioning time. The server's
// authenticateFleet middleware dispatches by token shape: JWT → JWT
// path; device token (no dots) → driver-tablet path.
import { getDriverTabletToken } from './driverTabletDeviceAuth';

/**
 * Stable error code emitted on every 401 from `apiFetch`. Components can
 * branch on `error.code === SESSION_EXPIRED_CODE` to render an
 * "expired session" message instead of a generic load failure. The
 * 401 interceptor in `apiFetch` clears the stale token and triggers a
 * hard navigation to /login?reason=expired before the error reaches the
 * caller, so this code is the defensive belt for any component that
 * happens to render in the microsecond window before the redirect.
 */
export const SESSION_EXPIRED_CODE = 'SESSION_EXPIRED';

/**
 * Has `apiFetch` already kicked off a redirect to /login during this
 * session? Module-level guard so a burst of parallel 401s only triggers
 * one navigation, not one per failing query.
 */
let redirectingForExpiredSession = false;

/**
 * THE 401 PATH, shared (2026-10-14, from the branch audit).
 *
 * This used to live inline in `apiFetch`, which was fine while apiFetch was
 * the only way out of the app. It is not: a multipart upload cannot go
 * through apiFetch, because apiFetch sets `Content-Type: application/json`
 * and a FormData body needs the browser to choose its own boundary. Those
 * two callers were therefore doing their own thing with a 401 — showing
 * "Upload failed (HTTP 401)" and leaving a dead token in place.
 *
 * Pulled out rather than copied, for the module-level guard. A burst of
 * parallel 401s must trigger ONE navigation, and a second copy of this with
 * its own flag would navigate on top of the first.
 *
 * Returns the error rather than throwing it, so a caller can `throw expiredSession()`
 * and keep its own control flow readable.
 */
export function expiredSession(details?: unknown, meta?: unknown): ApiError {
  clearAccessToken();
  if (
    typeof window !== 'undefined' &&
    !redirectingForExpiredSession &&
    !window.location.pathname.startsWith('/login')
  ) {
    // The Employee app (/employee) has its own sign-in: an employee whose
    // session ran out goes back to it, not to the staff login page.
    const toEmployeeApp = inEmployeeApp();
    redirectingForExpiredSession = true;
    // Hard navigation. The login page picks `?reason=expired` out of the
    // query string and renders the "Your session has expired" banner above
    // the form.
    window.location.assign(toEmployeeApp ? '/employee?reason=expired' : '/login?reason=expired');
  }
  return new ApiError(
    SESSION_EXPIRED_CODE,
    'Your session has expired. Please sign in again.',
    401,
    details as never,
    meta as never,
  );
}
/** Standard success envelope returned by every JeanieIQ route. */
interface ApiSuccess<T> {
  success: true;
  data: T;
}

/** Standard error envelope returned by every JeanieIQ route. */
interface ApiFailure {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

type ApiEnvelope<T> = ApiSuccess<T> | ApiFailure;

/**
 * Error thrown for any non-2xx response or for a 2xx response that contains
 * `success: false`. Carries the error code so UI code can branch on it
 * (e.g. show a tier-upgrade banner for `TIER_REQUIRED`).
 */
/** A reply that was not the platform's. The status goes to the Error Log, never on screen. */
export const NOT_REACHED_MESSAGE = "Couldn't reach JeanieIQ. Check your connection and try again in a moment.";

export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly details: unknown;
  /**
   * WHICH REQUEST, AND WHETHER THE PLATFORM ANSWERED IT (2026-10-02).
   *
   * The error box titled everything "That didn't happen", because nothing
   * reaching it knew whether the failure was a save, a delete or a load. Shane,
   * on the box: *"they need to state what didnt happen... if it failed to load
   * it needs to say failed to load! or failed to save"*. The method answers
   * that for every mutation at once, and the path is what makes a fault
   * traceable afterwards. No query string: it can carry what was typed.
   *
   * `reachedPlatform` is false when the reply was not the platform's own JSON
   * envelope. The API's terminal handler answers every crash with that
   * envelope (app.ts), so a reply without it never came from the API - measured
   * 2026-10-02: Vite's /api proxy answers 500 with an EMPTY text/plain body when
   * the API is down or restarting, and the box showed that as "Request failed
   * with status 500 · HTTP_ERROR". null where the body was not read.
   */
  public readonly request: { method: string; path: string } | null;
  public readonly reachedPlatform: boolean | null;
  /** The X-Request-Id this request carried, matching the server's log lines. */
  public readonly requestId: string | null;
  /** For the Error Log: why it never connected, or what answered instead of the platform. */
  public readonly detail: string | null;

  constructor(
    code: string,
    message: string,
    status: number,
    details?: unknown,
    meta?: { method?: string; path?: string; reachedPlatform?: boolean | null; requestId?: string | null; detail?: string | null },
  ) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
    this.request =
      meta?.method && meta.path ? { method: meta.method, path: meta.path.split('?')[0] ?? meta.path } : null;
    this.reachedPlatform = meta?.reachedPlatform ?? null;
    this.requestId = meta?.requestId ?? null;
    this.detail = meta?.detail ?? null;
  }
}

/**
 * fetch, with a request that never connected turned into an ApiError.
 *
 * A dropped connection makes fetch reject with a bare TypeError ("Failed to
 * fetch"), which reached the error box as exactly those words, with no code
 * and nothing to say which request it was. An abort is NOT a network failure
 * and is rethrown untouched: React Query cancels with it.
 */
async function fetchOrUnreachable(
  url: string,
  init: Omit<RequestInit, 'headers'> & { headers: Record<string, string> },
  method: string,
  path: string,
): Promise<Response> {
  // Sent only where the request is preflighted anyway (signed in, or a JSON
  // body), so it costs no extra round trip. Otherwise the server's echo is used.
  const preflighted = init.headers.Authorization !== undefined || init.headers['Content-Type'] === 'application/json';
  const sentId = preflighted ? newRequestId() : null;
  const headers = sentId ? { ...init.headers, 'X-Request-Id': sentId } : init.headers;
  const startedAt = Date.now();
  try {
    const res = await fetch(url, { ...init, headers });
    const id = sentId ?? res.headers.get('X-Request-Id');
    if (id) requestIds.set(res, id);
    return res;
  } catch (err) {
    if (err instanceof TypeError) {
      throw new ApiError('NETWORK_ERROR', 'Could not reach the server.', 0, undefined, {
        method,
        path,
        reachedPlatform: false,
        requestId: sentId,
        detail: unreachableDetail(err, startedAt),
      });
    }
    throw err;
  }
}

const requestIds = new WeakMap<Response, string>();

/** The request id a response belongs to, and the foreign-reply line when the platform did not answer. */
function trace(res: Response, text: string, reachedPlatform: boolean): { requestId: string | null; detail: string | null } {
  return { requestId: requestIds.get(res) ?? null, detail: reachedPlatform ? null : foreignReply(res, text) };
}

/**
 * The error a person can act on, rather than the one the envelope carries.
 *
 * WEB-VALIDATION-DETAIL-001 (2026-09-12). Found while chasing a Health & Safety
 * bug report — "adding a risk assessment says invalid request and does not
 * save". The server had said precisely what was wrong:
 *
 *     422  VALIDATION_ERROR   "Invalid request"
 *     details.issues[0]  path: nextReviewDate   "Expected YYYY-MM-DD"
 *
 * The screen showed "Invalid request". `ApiError` HAD THE DETAIL ALL ALONG —
 * `details` is populated by apiFetch and then read by nobody. Every form on the
 * platform renders `err.message`, so the one sentence that identifies the field
 * was carried across the wire and dropped at the last step. Finding that bug
 * took an investigation; with this it would have taken a glance.
 *
 * WHAT IT DELIBERATELY DOES NOT DO: invent wording. Zod's own message is what
 * the server chose and is usually the clearest English available ("Expected
 * YYYY-MM-DD", "Required"). Rewriting it here would put two authorities on one
 * sentence and guarantee they drift.
 *
 * Capped at three. A submission rejected on eight fields at once is a shape
 * problem rather than a typing problem, and eight lines of red is not more
 * useful than three and a count.
 */
export function describeApiError(error: unknown, fallback: string): string {
  if (!(error instanceof ApiError)) {
    return error instanceof Error && error.message ? error.message : fallback;
  }

  const issues = describeValidationIssues(error.details);
  if (!issues) return error.message || fallback;
  return `${error.message || fallback} — ${issues}`;
}

/** Options accepted by `apiFetch` — same shape as fetch's RequestInit minus body. */
export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * Plain JSON body — **stringified for you**. Pass the object, not JSON.
   *
   * `object`, NOT `unknown`, and that is the whole point of the type
   * (WEB-APIFETCH-BODY-UNTYPED-001, 2026-08-29). It was `unknown`, which made
   * `body: JSON.stringify(x)` compile perfectly — the server then received a JSON
   * STRING where it expected an object, Zod rejected it, and the screen said
   * "An unexpected error occurred". That cost a debugging session on
   * `updateDocumentContent` on 2026-08-28.
   *
   * NOTHING ELSE COULD HAVE CAUGHT IT, which is why the fix belongs in the type.
   * Component tests mock the client function. Integration tests use supertest,
   * which serialises properly and never runs this file. The type-check was the
   * only layer standing between the two, and `unknown` made it blind.
   *
   * Narrowing it flushed out no other caller — measured, zero type errors across
   * the app — so every existing call was already correct and this only closes
   * the door.
   *
   * WATCH OUT FOR THE NEIGHBOUR. `adminFetch` in `platformAdminApi.ts` takes a
   * raw `RequestInit` and does NOT stringify, so its 64 `body: JSON.stringify`
   * calls are all correct. Two wrappers, opposite contracts, call sites that look
   * identical. Do not "fix" those.
   */
  body?: object;
  /** Query string parameters — appended to the URL. Skipped when value is null/undefined. */
  query?: Record<string, string | number | boolean | null | undefined>;
  /** Extra headers merged on top of the defaults. */
  headers?: Record<string, string>;
  /** Abort signal for cancellation (react-query passes this through). */
  signal?: AbortSignal;
}

/**
 * Resolve the API base URL. We honour `VITE_API_BASE_URL` for explicit
 * overrides, but default to an empty string so the browser hits the same
 * origin (vite dev server proxies in dev, reverse-proxy serves in prod).
 *
 * THE EMPTY DEFAULT IS CORRECT — DO NOT "FIX" IT (2026-08-03).
 *
 * Callers already pass the full path including the prefix, e.g.
 * apiFetch('/api/dashboard'). Setting the base to '/api' would therefore
 * produce '/api/api/dashboard' and break every request in the platform.
 * Only ever set VITE_API_BASE_URL to a full origin (https://host), never
 * to a path.
 *
 * There used to be a separate `VITE_API_URL` set in .env, .env.example,
 * ci.yml, apps/web/Dockerfile, docker-compose.dev.yml and
 * deploy-preview.sh — six places configuring a name nothing here ever
 * read, so `--build-arg VITE_API_URL=/api` on every preview deploy did
 * precisely nothing. Removed rather than renamed: renaming it to the name
 * this function DOES read is exactly the '/api/api/...' breakage above.
 * Locked by the same-origin tests in api.test.ts.
 */
function getApiBase(): string {
  // Vite injects import.meta.env at build/dev time. The cast keeps TS quiet
  // when the variable isn't declared in vite-env.d.ts yet.
  const fromEnv = (import.meta as unknown as { env?: Record<string, string> }).env
    ?.VITE_API_BASE_URL;
  return (fromEnv ?? '').replace(/\/$/, '');
}

/**
 * The URL for an API path, for the callers that cannot use apiFetch — raw
 * fetch for multipart uploads, blob downloads and CSV exports.
 *
 * EVERY RAW fetch OF AN /api PATH MUST GO THROUGH THIS (2026-09-25). Production
 * serves the web app from S3 behind CloudFront and the API from its own host,
 * and CloudFront has no /api route: a bare fetch('/api/...') reaches S3, whose
 * 403 CloudFront's SPA error page turns into index.html with a 200. Every
 * upload in the platform failed that way while every apiFetch call worked. Dev
 * and preview never show it because their nginx proxies /api on the same
 * origin, so the base is empty there and this returns the path unchanged.
 *
 * Idempotent: a URL that is already absolute is returned as it is, so a helper
 * that builds its URL with this can be handed to another that applies it again.
 */
export function apiUrl(path: string): string {
  return path.startsWith('http') ? path : `${getApiBase()}${path}`;
}

/** Build a URL with optional query string parameters. */
function buildUrl(path: string, query?: ApiFetchOptions['query']): string {
  const base = getApiBase();
  const url = path.startsWith('http') ? path : `${base}${path}`;
  if (!query) {
    return url;
  }
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') {
      continue;
    }
    params.append(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

/**
 * The active branch id, mirrored here so every request carries X-Branch-Id
 * without each caller threading it through. Set by BranchProvider; null until
 * a branch is chosen, in which case the server falls back to the tenant's
 * default nation.
 */
let activeBranchId: string | null = null;

/** Set (or clear) the branch id attached to every subsequent request. */
export function setActiveBranchId(id: string | null): void {
  activeBranchId = id;
}

/**
 * Make an authenticated request to the JeanieIQ API and return the unwrapped
 * `data` payload. Throws `ApiError` on failure.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const url = buildUrl(path, options.query);
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(options.headers ?? {}),
  };
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  // Nation split (2026-08-24) — the active branch decides which nation's rules
  // the request runs under. The client only names a BRANCH; the server resolves
  // the nation from its own registry and never trusts a client-sent nation.
  if (activeBranchId) {
    headers['X-Branch-Id'] = activeBranchId;
  }
  const token = getAccessToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
    // Which screen asked, so a crash the server writes to the Error Log says
    // so (2026-10-02). Only on signed-in requests, which are preflighted for
    // their Authorization header already, so it costs no extra round trip.
    const screen = currentScreen();
    if (screen) headers['X-Client-Screen'] = screen;
  } else {
    // T1.2 phase 2 — cold driver tablet has no JWT but does have a
    // device token from provisioning. Attach it so /api/fleet routes
    // can authenticate via the driver-tablet path.
    const driverTabletToken = getDriverTabletToken();
    if (driverTabletToken) {
      headers.Authorization = `Bearer ${driverTabletToken}`;
    }
  }

  const method = options.method ?? 'GET';
  const response = await fetchOrUnreachable(
    url,
    {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal,
      credentials: 'same-origin',
    },
    method,
    path,
  );

  // Try to parse the body as JSON regardless of status — JeanieIQ always
  // returns the standard envelope, even for errors.
  // Not JSON (a proxy's 502 page, or a 204 No Content) leaves payload null;
  // the text is kept to say what answered.
  const reply = await readReply(response);
  const payload = reply.json as ApiEnvelope<T> | null;

  // 204 No Content is a success with no envelope (the Document Register DELETE
  // returns it); its empty body leaves `payload` null, which the check below
  // would otherwise treat as an error.
  if (response.ok && payload === null && response.status === 204) {
    return undefined as T;
  }

  if (!response.ok || !payload || payload.success === false) {
    const code = payload && payload.success === false ? payload.error.code : 'HTTP_ERROR';
    const message =
      payload && payload.success === false
        ? payload.error.message
        : NOT_REACHED_MESSAGE;
    const details = payload && payload.success === false ? payload.error.details : undefined;
    const reachedPlatform = payload !== null && payload.success === false;
    const meta = { method, path, reachedPlatform, ...trace(response, reply.text, reachedPlatform) };

    // 401 interceptor — clear the stale token and bounce to /login. Only
    // fires when there WAS an Authorization header on this request, so we
    // don't recursively redirect a request that intentionally went out
    // unauthenticated (e.g. /api/auth/login itself). Module-level guard
    // ensures parallel 401s in the same render only trigger one navigation.
    const wasAuthenticated = headers.Authorization !== undefined;
    if (response.status === 401 && wasAuthenticated) {
      throw expiredSession(details, meta);
    }

    throw new ApiError(code, message, response.status, details, meta);
  }

  return payload.data;
}

/**
 * Trigger an authenticated file download. Used by section Import/Export
 * tabs for Export CSV + Template CSV buttons (Session 3.18).
 *
 * The server returns the file as a binary body (not the JSON envelope),
 * so apiFetch doesn't suit. We fetch with the Bearer token, convert to a
 * Blob, and invoke a transient <a download> click to save it.
 */
export async function apiDownload(
  path: string,
  filename: string,
  options: {
    query?: Record<string, string | number | null | undefined>;
    /** Defaults to GET. Pass 'POST' with `body` for report endpoints
     *  that take parameters (additive — existing GET callers unchanged). */
    method?: 'GET' | 'POST';
    body?: object;
  } = {},
): Promise<void> {
  const url = buildUrl(path, options.query);
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  const method = options.method ?? 'GET';
  if (method === 'POST') headers['Content-Type'] = 'application/json';

  const response = await fetchOrUnreachable(
    url,
    {
      method,
      headers,
      credentials: 'same-origin',
      ...(method === 'POST' && options.body !== undefined
        ? { body: JSON.stringify(options.body) }
        : {}),
    },
    method,
    path,
  );
  if (!response.ok) {
    /* Read only to learn whether the platform answered; the message is kept. */
    const reply = await readReply(response);
    const reachedPlatform = (reply.json as { success?: boolean } | null)?.success === false;
    throw new ApiError(
      'DOWNLOAD_FAILED',
      'The download failed. Try again in a moment.',
      response.status,
      undefined,
      { method, path, reachedPlatform, ...trace(response, reply.text, reachedPlatform) },
    );
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

/*
 * ════════════════════════════════════════════════════════════════════
 * ONE WAY TO OPEN A STORED DOCUMENT, AND ONE WAY TO SAVE IT.
 *
 * Shane, 2026-09-22: *"it just needs to be 1 way of downloading a
 * documents and re opening"*.
 *
 * THERE WERE FOUR, and each one knew something the others did not:
 *
 *   apiOpenInNewTab      path-based, handles a blocked popup, NO inline-safe
 *                        rule, no filename from the header, no 401 handling
 *   apiDownload          path-based, no 401 handling, caller supplies the name
 *   previewAttachment    inline-safe rule, filename from the header, 401
 *                        handling — but hard-wired to one endpoint
 *   previewDocumentVersion   the same code again, hard-wired to another
 *
 * So which safety checks you got depended on which screen you were on. The
 * pair below is the union of all four, and the three specialised functions
 * are now thin wrappers over it that keep their own call sites unchanged.
 *
 * WHY THE INLINE-SAFE RULE MATTERS, since it is the one that was missing in
 * the general helper. A blob URL inherits the APP's origin, so opening an
 * uploaded .html or .svg in a tab runs it as this application, with the
 * session it can reach. Only a PDF or an image may be opened; anything else
 * is saved to disk instead, where it is inert. `apiOpenInNewTab` had no such
 * rule, and one of its five call sites — the Vault's Open button — points at
 * arbitrary uploaded files. That is closed by this consolidation rather than
 * by a separate fix.
 * ════════════════════════════════════════════════════════════════════
 */

/**
 * Only these may be rendered in a tab; see the block above for why.
 *
 * SVG IS EXCLUDED, and that is not pedantry. `image/svg+xml` starts with
 * "image/", so the rule both old copies used — `type.startsWith('image/')` —
 * let an uploaded SVG open on this application's origin, and an SVG carries
 * <script> exactly as HTML does. Caught 2026-09-22 by the first test written
 * against this consolidated helper; both of the copies it replaces had it.
 */
function mayOpenInline(mimeType: string): boolean {
  if (mimeType === 'application/pdf') return true;
  if (mimeType === 'image/svg+xml') return false;
  return mimeType.startsWith('image/');
}

/** The server's own name for the file, which beats anything the caller guessed. */
function filenameFromHeaders(res: Response, fallback: string): string {
  const disposition = res.headers.get('Content-Disposition') ?? '';
  return /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback;
}

/** Save a blob under `filename` via a transient anchor. */
export function saveBlob(blob: Blob, filename: string): void {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

/**
 * Fetch a stored document with the bearer token, failing the way the rest of
 * the client does. A top-level navigation cannot carry the token, which is why
 * every one of these goes through fetch and a blob rather than an href.
 */
async function fetchDocument(
  path: string,
  query: Record<string, string | number | null | undefined> | undefined,
  failureCode: string,
): Promise<Response> {
  const url = buildUrl(path, query);
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetchOrUnreachable(url, { method: 'GET', headers, credentials: 'same-origin' }, 'GET', path);

  if (res.status === 401) {
    clearSessionAfter401();
    throw new ApiError(
      SESSION_EXPIRED_CODE,
      'Your session has expired. Please sign in again.',
      401,
      undefined,
      { method: 'GET', path, reachedPlatform: true },
    );
  }
  if (!res.ok) {
    // The server's message where there is one — "No document attached to this
    // contract" tells a person what to do; "failed with status 404" does not.
    const reply = await readReply(res);
    const body = reply.json as { success?: boolean; error?: { message?: string } } | null;
    const reachedPlatform = body?.success === false;
    const message = body?.error?.message ?? NOT_REACHED_MESSAGE;
    throw new ApiError(failureCode, message, res.status, undefined, { method: 'GET', path, reachedPlatform, ...trace(res, reply.text, reachedPlatform) });
  }
  return res;
}

/**
 * Show a document in the page's own viewer when that is safe, save it when it
 * is not. Every View and Print ends here; never window.open a document.
 */
/** A file from an authenticated API path (a PDF, an upload), as a Blob. */
export async function fetchAuthedBlob(url: string): Promise<Blob> {
  const token = getAccessToken();
  const res = await fetch(apiUrl(url), {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: { message?: string } };
    throw new Error(body.error?.message ?? 'Could not open the file. Try again in a moment.');
  }
  return res.blob();
}

export function showDocument(blob: Blob, filename: string): void {
  if (!mayOpenInline(blob.type || '')) {
    saveBlob(blob, filename);
    return;
  }
  showInPage(blob, filename);
}

/** Open a stored document by its path. Prefer it over a fetch of your own. */
export async function openDocument(
  path: string,
  fallbackFilename = 'document',
  options: { query?: Record<string, string | number | null | undefined> } = {},
): Promise<void> {
  const res = await fetchDocument(path, options.query, 'OPEN_FAILED');
  showDocument(await res.blob(), filenameFromHeaders(res, fallbackFilename));
}

/** Print a document: its PDF opens in the viewer, whose own Print prints it. */
export async function openPrint(
  path: string,
  fallbackFilename = 'document.pdf',
  options: { query?: Record<string, string | number | null | undefined> } = {},
): Promise<void> {
  await openDocument(path, fallbackFilename, options);
}

/** A stored document's bytes and its server name, for a caller that packs several into one save. */
export async function fetchDocumentFile(
  path: string,
  fallbackFilename = 'document',
): Promise<{ blob: Blob; filename: string }> {
  const res = await fetchDocument(path, undefined, 'DOWNLOAD_FAILED');
  return { blob: await res.blob(), filename: filenameFromHeaders(res, fallbackFilename) };
}

/** Save a stored document to disk. The one way; never opens a tab. */
export async function saveDocument(
  path: string,
  fallbackFilename = 'document',
  options: { query?: Record<string, string | number | null | undefined> } = {},
): Promise<void> {
  const res = await fetchDocument(path, options.query, 'DOWNLOAD_FAILED');
  const filename = filenameFromHeaders(res, fallbackFilename);
  saveBlob(await res.blob(), filename);
}

/** The old name for openDocument, kept for its callers; despite the name it opens no tab. */
export async function apiOpenInNewTab(
  path: string,
  options: {
    query?: Record<string, string | number | null | undefined>;
  } = {},
): Promise<void> {
  return openDocument(path, 'document', options);
}

/**
 * GET a binary and hand it back as an object URL, for playing or showing it
 * inside the page.
 *
 * WHY THIS EXISTS, AND WHAT IT COSTS. A <video src> cannot carry an
 * Authorization header, and authenticateToken reads the bearer from that header
 * and nowhere else - no cookie, no query parameter. So a protected video cannot
 * be handed to the element as a URL, and the only thing the browser will accept
 * is a blob we fetched ourselves.
 *
 * The cost is real and worth stating plainly: this downloads the WHOLE file
 * before a single frame plays, which is exactly what the Range support on
 * /api/how-to/media/:id was built to avoid. Seeking is then instant, because
 * the bytes are already local - so for the short platform help videos this is
 * a slower start and a better scrubber, not a broken feature.
 *
 * IT DOES NOT SCALE TO LONG VIDEO. The honest fix is a short-lived signed URL
 * the element can be given directly, at which point Range does the work it was
 * written for. Not built yet; flagged when the first long video appears.
 *
 * THE CALLER OWNS THE URL - revoke it when swapping or unmounting, or the
 * blob stays in memory for the life of the tab. A video blob is tens of
 * megabytes, so this matters more here than for a PDF preview.
 */
export async function apiGetObjectUrl(path: string, signal?: AbortSignal): Promise<string> {
  const url = buildUrl(path);
  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetchOrUnreachable(
    url,
    {
      method: 'GET',
      headers,
      credentials: 'same-origin',
      ...(signal ? { signal } : {}),
    },
    'GET',
    path,
  );
  if (!response.ok) {
    const reply = await readReply(response);
    const parsed = reply.json as { success?: boolean; error?: { code?: string; message?: string } } | null;
    const reachedPlatform = parsed?.success === false;
    const message = parsed?.error?.message ?? NOT_REACHED_MESSAGE;
    const code = parsed?.error?.code ?? 'HTTP_ERROR';
    throw new ApiError(code, message, response.status, undefined, { method: 'GET', path, reachedPlatform, ...trace(response, reply.text, reachedPlatform) });
  }

  return URL.createObjectURL(await response.blob());
}

/**
 * POST a JSON body and get the binary response back as an object URL, for
 * showing a server-rendered document INSIDE the page rather than in a new tab.
 *
 * The existing helpers do not cover this: apiDownload saves a file and
 * apiOpenInNewTab is a GET. The invoice branding preview needs a POST — the
 * body carries the unsaved settings being previewed — and needs the result in
 * an <iframe>, because a preview you have to leave the page to see is not one
 * you will check every time you nudge a colour.
 *
 * THE CALLER OWNS THE URL. There is no timed revoke here, unlike
 * apiOpenInNewTab where the new tab is gone from our reach: an embedded
 * preview is replaced every time a setting changes, so revoking on a timer
 * would blank a live iframe while the reader is looking at it. The caller
 * revokes the previous URL when it swaps in the next one, and on unmount.
 */
export async function apiPostForObjectUrl(
  path: string,
  body: unknown,
): Promise<string> {
  const url = buildUrl(path);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetchOrUnreachable(
    url,
    {
      method: 'POST',
      headers,
      credentials: 'same-origin',
      body: JSON.stringify(body ?? {}),
    },
    'POST',
    path,
  );
  if (!response.ok) {
    /*
     * The body is JSON on an error even though the success path is binary, so
     * the server's own message survives instead of being replaced by a status
     * code. A 400 from the branding endpoint says which field is wrong, and
     * that is exactly what the reader needs.
     */
    const reply = await readReply(response);
    const parsed = reply.json as { success?: boolean; error?: { code?: string; message?: string } } | null;
    const reachedPlatform = parsed?.success === false;
    const message = parsed?.error?.message ?? NOT_REACHED_MESSAGE;
    const code = parsed?.error?.code ?? 'REQUEST_FAILED';
    throw new ApiError(code, message, response.status, undefined, { method: 'POST', path, reachedPlatform, ...trace(response, reply.text, reachedPlatform) });
  }

  return URL.createObjectURL(await response.blob());
}

/**
 * Test-only — reset the in-flight redirect guard so each test gets a
 * clean slate. Never call this in production code.
 */
export function __resetSessionExpiredGuardForTests(): void {
  redirectingForExpiredSession = false;
}
